import { useMemo } from "react";

import { useLocalization } from "@fluent/react";
import { groupAccountsByOrganization } from "byzantine";

import { useAccountsContext } from "../../../../../components/contexts/AccountContext";
import { useCurrentUser } from "../../../../../components/contexts/CurrentUserContext";
import { LIVE_LEDGER_ACCOUNT_QUERY_KEY } from "../../constants";
import { useIsMultiEntity } from "../../hooks/useIsMultiEntity";
import { useMultiSelectFilter } from "../../hooks/useMultiSelectFilter";

import MultiSelectFilterPanel from "./MultiSelectFilterPanel";

import type Account from "byzantine/src/Account";

export interface AccountFilterPanelProps {
  close: () => void;
  remove: () => void;
  onApply: () => void;
}

// Sized to fit the 300px panel alongside the masked-number suffix.
const MAX_ACCOUNT_NAME_LENGTH = 21;

export const getAccountLabel = (account: Account): string => {
  const name = account.getName();
  const maskedNumber = account.getMaskedNumber();
  const truncatedName =
    name.length > MAX_ACCOUNT_NAME_LENGTH
      ? `${name.slice(0, MAX_ACCOUNT_NAME_LENGTH)}…`
      : name;
  return maskedNumber ? `${truncatedName}-${maskedNumber}` : truncatedName;
};

// Search the full name, not the truncated label.
const getAccountSearchText = (option: {
  value: string;
  label: string;
}): string => {
  // The label is already truncated; search text needs to come from the
  // original account object. We encode the full text into a data attribute
  // on the option at build time (see accountOptions below).
  return (option as AccountOption).searchText;
};

interface AccountOption {
  value: string;
  label: string;
  searchText: string;
  group?: string;
}

const AccountFilterPanel = ({
  close,
  remove,
  onApply,
}: AccountFilterPanelProps) => {
  const { l10n } = useLocalization();
  const { accounts: rawAccounts } = useAccountsContext();
  const accounts = useMemo(
    () => (rawAccounts ?? []).filter((account) => account.isInternal()),
    [rawAccounts]
  );
  const { currentUser } = useCurrentUser();
  const isMultiEntity = useIsMultiEntity(accounts);

  const accountOptions: AccountOption[] = useMemo(() => {
    const toOption = (account: Account, group?: string): AccountOption => ({
      value: account.id,
      label: getAccountLabel(account),
      searchText: `${account.getName()} ${
        account.getMaskedNumber() ?? ""
      }`.toLowerCase(),
      group,
    });

    if (!isMultiEntity) return accounts.map((account) => toOption(account));

    // Fallback matches useIsMultiEntity's grouping so accounts with no
    // `organization` (e.g. personal accounts) aren't dropped from the list.
    return Array.from(
      groupAccountsByOrganization(accounts, currentUser?.org_name)
    ).flatMap(([orgName, orgAccounts]) =>
      orgAccounts.map((account) => toOption(account, orgName))
    );
  }, [accounts, isMultiEntity, currentUser?.org_name]);

  const validAccountIds = useMemo(
    () => accounts.map((account) => account.id),
    [accounts]
  );

  const { selectedValues, setSelectedValues } = useMultiSelectFilter({
    queryKey: LIVE_LEDGER_ACCOUNT_QUERY_KEY,
    validValues: validAccountIds,
  });

  return (
    <MultiSelectFilterPanel
      options={accountOptions}
      selectedValues={selectedValues}
      setSelectedValues={setSelectedValues}
      close={close}
      remove={remove}
      onApply={onApply}
      searchLabel={l10n.getString("live-ledger-filter-account-search")}
      emptyLabel={l10n.getString("live-ledger-filter-account-empty")}
      applyLabel={l10n.getString("live-ledger-filter-account-apply")}
      removeLabel={l10n.getString("live-ledger-filter-account-remove")}
      testIdPrefix="ledger-account-filter"
      getSearchText={getAccountSearchText}
      groupSelectable={isMultiEntity}
    />
  );
};

export default AccountFilterPanel;
